import { useCallback, useMemo, useState } from 'react'
import { config } from '@/lib/config.runtime'
import type { AuthState } from '@/hooks/useAuth'
import type { RoutePath } from '@/hooks/useHashRoute'
import { useFeedbackDaily } from '@/hooks/useFeedbackDaily'
import { defaultRange } from '@/lib/analytics/range'
import AccountMenu from '@/components/AccountMenu'
import DateRangeControl from '@/components/DateRangeControl'
import FeedbackInsights from '@/components/FeedbackInsights'
import Logo from '@/components/Logo'
import UsageAnalytics from '@/components/UsageAnalytics'
import styles from './Statistics.module.css'

export type StatisticsProps = {
  auth: Extract<AuthState, { kind: 'authed' }>
  onNavigate: (path: RoutePath) => void
}

/** Admin-gated dashboard collecting every plot behind one shared from/to date
 * range: usage analytics and the feedback-per-day chart move here off the
 * admin panel, on a wider canvas with its own #/stats route. */
export default function Statistics({ auth, onNavigate }: StatisticsProps) {
  const acquireToken = useCallback(() => auth.acquireToken(), [auth])

  const [range, setRange] = useState(() => defaultRange())

  const feedback = useFeedbackDaily({
    baseUrl: config.apiBaseUrl,
    acquireToken,
    from: useMemo(() => new Date(range.from), [range.from]),
    to: useMemo(() => new Date(range.to), [range.to]),
  })

  return (
    <main className={styles.main}>
      <header className={styles.header}>
        <div className={styles.brand}>
          <button
            className={styles.back}
            onClick={() => onNavigate('chat')}
            aria-label="Tilbake til chat"
          >
            ←
          </button>
          <div className={styles.lockup}>
            <Logo variant="text" decorative className={styles.logoText} />
            <Logo variant="icon" className={styles.logoIcon} />
          </div>
          <span className={styles.tagline}>
            <strong>Statistikk</strong>
          </span>
        </div>
        <AccountMenu
          username={auth.account.username}
          onLogout={() => void auth.logout()}
          currentPage="stats"
          onNavigate={onNavigate}
          isAdmin
        />
      </header>

      <div className={styles.content}>
        <div className={styles.rangeBar}>
          <DateRangeControl from={range.from} to={range.to} onChange={setRange} />
        </div>

        <UsageAnalytics acquireToken={acquireToken} from={range.from} to={range.to} />

        <FeedbackInsights state={feedback.state} />
      </div>
    </main>
  )
}
